import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { parseUserInput, formatXmr, piconeroToXmr } from '@/lib/monero/units';
import { piconeroToFiat, fiatToPiconero, getFiatCurrency } from '@/lib/monero/fiat';

interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  unit: 'xmr' | 'piconero' | `fiat:${string}`;
  onUnitToggle: () => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  max?: number;
  fiatPrice?: number | null; // XMR price in fiat, null = loading
}

export function AmountInput({
  value,
  onChange,
  unit,
  onUnitToggle,
  label,
  placeholder,
  disabled,
  max,
  fiatPrice,
}: AmountInputProps) {
  const [focused, setFocused] = useState(false);

  const isFiat = unit.startsWith('fiat:');
  const fiatCode = isFiat ? unit.slice(5) : '';
  const fiatCurrency = isFiat ? getFiatCurrency(fiatCode) : null;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (unit === 'xmr' || isFiat) {
      if (/^[0-9]*\.?[0-9]*$/.test(raw) || raw === '') {
        onChange(raw);
      }
    } else {
      if (/^[0-9]*$/.test(raw) || raw === '') {
        onChange(raw);
      }
    }
  }, [unit, isFiat, onChange]);

  const unitLabel = isFiat ? fiatCurrency?.symbol ?? fiatCode : unit === 'xmr' ? 'XMR' : 'piconero';
  const inputMode: 'decimal' | 'numeric' = (unit === 'xmr' || isFiat) ? 'decimal' : 'numeric';

  const converted = value && fiatPrice
    ? (isFiat
        ? `${formatXmr(fiatToPiconero(parseFloat(value || '0'), fiatPrice), 6)} XMR`
        : `${piconeroToFiat(
            parseUserInput(value || '0', unit as 'xmr' | 'piconero'),
            fiatPrice,
          )} ${fiatCode}`)
    : '';

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <div className="relative">
        <Input
          type="text"
          inputMode={inputMode}
          value={value}
          onChange={handleChange}
          placeholder={placeholder ?? (unit === 'piconero' ? '0' : '0.00')}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="pr-20 text-lg font-mono"
        />
        <button
          type="button"
          onClick={onUnitToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium px-2 py-1 rounded bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
        >
          {unitLabel}
        </button>
      </div>
      {value && !focused && converted && (
        <p className="text-xs text-muted-foreground">
          ≈ {converted}
        </p>
      )}
      {value && !focused && isFiat && !fiatPrice && (
        <p className="text-xs text-muted-foreground">Fetching price...</p>
      )}
      {max && value && (
        <p className="text-xs text-destructive">
          Max: {unit === 'xmr' ? formatXmr(max, 6) + ' XMR' : isFiat && fiatPrice ? `$ ${piconeroToFiat(max, fiatPrice)}` : max.toLocaleString()}
        </p>
      )}
    </div>
  );
}
